import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { absenceChipStyle } from '../../../utils/absenceColors';
import { Car, Plus, Umbrella } from 'lucide-react';
import type { FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto } from '../../../api/generated/models';
import { EmployeeAvatar } from '../../../components/Shared/EmployeeAvatar';
import { getObjectColors } from '../../../utils/objectColors';
import { getServiceIcon } from '../../../utils/serviceIcons';
import { pctOf, timeToMin } from '../../../utils/timeAxis';
import { DUE_TASK_DRAG_TYPE, type DueTaskDragData } from './DueTasksSidepanel';

type Entry = FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto;
type EntryTask = NonNullable<Entry['tasks']>[number];

interface TimelineRowProps {
  /** Either an actual entry or a placeholder for an employee without one */
  entry?: Entry;
  employeeId: string;
  employeeName: string;
  employeeAvatarColor?: string | null;
  /** Verwaltung/SuperAdmin — their absences are drawn solid instead of tinted (FACF-94). */
  isBackOffice?: boolean;
  date: string;
  axis: { startMin: number; total: number; hours: number[] };
  isToday: boolean;
  employeeColumnWidth?: number;
  onTaskClick?: (task: EntryTask, entry: Entry) => void;
  /** Click on empty area of the timeline at this minute-of-day → add task there. */
  onAddTask?: (entry: Entry, startTime: string) => void;
  onAddWorkDay?: (employeeId: string, date: string) => void;
  onDueTaskDrop?: (
    taskId: string,
    employeeId: string,
    date: string,
    durationMinutes: number,
    startTime?: string,
  ) => void;
}

const ROW_HEIGHT = 68;

function roundToFive(min: number): number {
  return Math.max(0, Math.round(min / 5) * 5);
}

function formatHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isAbsence(entry: Entry): boolean {
  return (entry.entryType ?? '').toLowerCase() === 'absence';
}

function detectOverlaps(tasks: EntryTask[]): Set<string> {
  const conflicts = new Set<string>();
  for (let i = 0; i < tasks.length; i++) {
    const a = tasks[i];
    if (!a.startTime || !a.endTime) continue;
    const aStart = timeToMin(a.startTime);
    const aEnd = timeToMin(a.endTime);
    for (let j = i + 1; j < tasks.length; j++) {
      const b = tasks[j];
      if (!b.startTime || !b.endTime) continue;
      const bStart = timeToMin(b.startTime);
      const bEnd = timeToMin(b.endTime);
      if (aStart < bEnd && bStart < aEnd) {
        if (a.planningEntryTaskId) conflicts.add(a.planningEntryTaskId);
        if (b.planningEntryTaskId) conflicts.add(b.planningEntryTaskId);
      }
    }
  }
  return conflicts;
}

export function TimelineRow({
  entry,
  employeeId,
  employeeName,
  employeeAvatarColor,
  isBackOffice = false,
  date,
  axis,
  isToday,
  employeeColumnWidth = 200,
  onTaskClick,
  onAddTask,
  onAddWorkDay,
  onDueTaskDrop,
}: TimelineRowProps) {
  const { t } = useTranslation('planning');
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });

  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, 60_000);
    return () => window.clearInterval(id);
  }, [isToday]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!onDueTaskDrop) return;

    const raw = e.dataTransfer.getData(DUE_TASK_DRAG_TYPE);
    if (!raw) return;
    let data: DueTaskDragData;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }

    let startTime: string | undefined;
    const track = trackRef.current;
    if (track) {
      const rect = track.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const pct = rect.width > 0 ? x / rect.width : 0;
      const minutes = axis.startMin + pct * axis.total;
      startTime = formatHHMM(roundToFive(minutes));
    }

    onDueTaskDrop(data.taskId, employeeId, date, data.estimatedDurationMinutes, startTime);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!onDueTaskDrop) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  // Empty-employee row (no entry for this day)
  if (!entry) {
    return (
      <div
        className="flex border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
        style={{ height: ROW_HEIGHT }}
      >
        <div
          className="shrink-0 flex items-center gap-2 px-3 border-r border-gray-200 dark:border-gray-700"
          style={{ width: employeeColumnWidth }}
        >
          <EmployeeAvatar
            employee={{ id: employeeId, name: employeeName, avatarColor: employeeAvatarColor }}
            size={28}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
            {employeeName}
          </span>
        </div>
        <div className="relative flex-1">
          {onAddWorkDay && (
            <button
              type="button"
              onClick={() => onAddWorkDay(employeeId, date)}
              className="
                group absolute inset-0 flex items-center justify-center gap-2
                text-ink-faint dark:text-gray-600 hover:text-primary
                hover:bg-primary/5 dark:hover:bg-primary/10 transition
              "
            >
              <span
                className="
                  w-6 h-6 rounded-full border border-dashed border-current
                  grid place-items-center
                "
              >
                <Plus size={12} />
              </span>
              <span className="text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {t('timeline.addEntry')}
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Absence row
  if (isAbsence(entry)) {
    return (
      <div
        className="
          flex border-b border-gray-100 dark:border-gray-800
          bg-surface-muted dark:bg-gray-800/60 bg-stripe-soft
        "
        style={{ height: ROW_HEIGHT }}
      >
        <div
          className="shrink-0 flex items-center gap-2 px-3 border-r border-gray-200 dark:border-gray-700"
          style={{ width: employeeColumnWidth }}
        >
          <EmployeeAvatar
            employee={{ id: employeeId, name: employeeName, avatarColor: employeeAvatarColor }}
            size={28}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
            {employeeName}
          </span>
        </div>
        <div className="relative flex-1 flex items-center px-3">
          <span
            className="flex items-center gap-2 px-2 py-1 rounded text-xs font-medium"
            style={absenceChipStyle(entry.absenceTypeColor, isBackOffice)}
            title={
              isBackOffice
                ? t('absenceBackOffice', { type: entry.absenceTypeName ?? '' })
                : undefined
            }
          >
            <Umbrella className="w-4 h-4 shrink-0" />
            {entry.absenceTypeName ?? 'Absence'}
          </span>
        </div>
      </div>
    );
  }

  // Workday row
  const tasks = (entry.tasks ?? []).filter((t) => t.startTime && t.endTime);
  const conflictIds = detectOverlaps(tasks);

  // Work-shift band (entry start/end)
  let shiftLeft: number | null = null;
  let shiftWidth: number | null = null;
  if (entry.startTime && entry.endTime) {
    shiftLeft = pctOf(timeToMin(entry.startTime), axis.startMin, axis.total);
    const right = pctOf(timeToMin(entry.endTime), axis.startMin, axis.total);
    shiftWidth = Math.max(0, right - shiftLeft);
  }

  const plannedH = Math.round(((entry.plannedHours ?? 0) + Number.EPSILON) * 10) / 10;
  const targetH = Math.round(((entry.targetHours ?? 0) + Number.EPSILON) * 10) / 10;

  return (
    <div
      className="flex border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
      style={{ height: ROW_HEIGHT }}
    >
      <div
        className="shrink-0 flex items-center gap-2 px-3 border-r border-gray-200 dark:border-gray-700 min-w-0"
        style={{ width: employeeColumnWidth }}
      >
        <EmployeeAvatar employee={{ id: employeeId, name: employeeName }} size={28} />
        <div className="min-w-0 flex-1">
          <div
            className="text-xs font-medium text-gray-900 dark:text-white truncate"
            title={employeeName}
          >
            {employeeName}
          </div>
          {targetH > 0 && (
            <div className="text-[10px] text-ink-muted dark:text-text-light text-tabular">
              {t('summary.dayHours', { planned: plannedH, target: targetH })}
            </div>
          )}
          {entry.plannedLicensePlate && (
            <div
              className="flex items-center gap-1 text-[10px] font-mono text-slate-600 dark:text-slate-300"
              title={t('plannedVehicle', { plate: entry.plannedLicensePlate })}
            >
              <Car className="w-3 h-3" aria-hidden />
              {entry.plannedLicensePlate}
            </div>
          )}
        </div>
      </div>

      <div
        ref={trackRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={(e) => {
          if (!onAddTask) return;
          const track = trackRef.current;
          if (!track) return;
          const rect = track.getBoundingClientRect();
          if (rect.width <= 0) return;
          const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
          const minutes = axis.startMin + (x / rect.width) * axis.total;
          onAddTask(entry, formatHHMM(roundToFive(minutes)));
        }}
        title={onAddTask ? t('actions.createEntry') : undefined}
        className={`
          relative flex-1
          ${onAddTask ? 'cursor-cell' : ''}
          ${isDragOver ? 'bg-primary/5' : ''}
        `}
      >
        {/* Hour grid lines */}
        {axis.hours.map((h, i) => {
          if (i === 0) return null;
          const left = (i / (axis.hours.length - 1)) * 100;
          const isNoon = h === 12;
          return (
            <div
              key={h}
              className={`absolute top-0 bottom-0 border-l ${
                isNoon
                  ? 'border-gray-300 dark:border-gray-600'
                  : 'border-gray-100 dark:border-gray-800'
              }`}
              style={{ left: `${left}%` }}
            />
          );
        })}

        {/* Work-shift band (passive visual indicator) */}
        {shiftLeft !== null && shiftWidth !== null && shiftWidth > 0 && (
          <div
            className="
              absolute top-2 bottom-2 rounded-md bg-surface-muted dark:bg-gray-800/80
              border border-border-subtle dark:border-gray-700 pointer-events-none
            "
            style={{ left: `${shiftLeft}%`, width: `${shiftWidth}%` }}
            aria-hidden
          />
        )}

        {/* Tasks */}
        {tasks.map((task, idx) => {
          if (!task.startTime || !task.endTime) return null;
          const start = timeToMin(task.startTime);
          const end = timeToMin(task.endTime);
          const left = pctOf(start, axis.startMin, axis.total);
          const right = pctOf(end, axis.startMin, axis.total);
          const width = Math.max(0.5, right - left);
          const { color, soft, ink } = getObjectColors(task.objectId ?? task.objectName);
          const ServiceIcon = getServiceIcon(task.serviceName);
          const displayName = task.objectShortName || task.objectName || task.taskName || '–';
          const inConflict = task.planningEntryTaskId
            ? conflictIds.has(task.planningEntryTaskId)
            : false;

          return (
            <button
              type="button"
              key={task.planningEntryTaskId || idx}
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick?.(task, entry);
              }}
              title={`${task.taskName ?? ''} · ${task.objectName ?? ''}\n${task.startTime}–${task.endTime}`}
              className={`
                absolute top-3 bottom-3 rounded-md text-left overflow-hidden
                transition-shadow hover:shadow-sm cursor-pointer
                ${inConflict ? 'ring-conflict' : ''}
              `}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                background: soft,
                borderLeft: `3px solid ${color}`,
                color: ink,
              }}
            >
              <div className="px-1.5 py-1 min-w-0 h-full flex flex-col justify-center">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-tabular leading-tight">
                  <ServiceIcon size={10} strokeWidth={2.25} className="shrink-0" />
                  <span className="truncate">{displayName}</span>
                </div>
                {width > 6 && (
                  <div
                    className="text-[9.5px] text-tabular truncate leading-tight opacity-80"
                  >
                    {task.startTime}–{task.endTime}
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Now-indicator */}
        {isToday && nowMin >= axis.startMin && nowMin <= axis.startMin + axis.total && (
          <div
            className="absolute top-0 bottom-0 w-px bg-status-conflict pointer-events-none z-10"
            style={{ left: `${pctOf(nowMin, axis.startMin, axis.total)}%` }}
            aria-label={t('timeline.now', { time: formatHHMM(nowMin) })}
          >
            <div className="absolute -top-1 -left-[3px] w-1.5 h-1.5 rounded-full bg-status-conflict" />
          </div>
        )}
      </div>
    </div>
  );
}
